import { Download, FileText, FileType, Image as ImageIcon } from "lucide-react";
import * as React from "react";
import config from "src/common/config";
import { getGlobalHeaders } from "src/common/relay-env";
import { cn } from "src/common/utils";

interface MessageAttachmentType {
  originalName: string;
  mimetype: string;
  size?: number | null;
  downloadToken: string;
}

interface MessageAttachmentListProps {
  attachments: readonly MessageAttachmentType[];
  className?: string;
}

const formatSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
};

const FileIcon: React.FC<{ mimetype: string }> = ({ mimetype }) => {
  if (mimetype.startsWith("image/")) return <ImageIcon className="h-5 w-5 text-muted-foreground" />;
  if (mimetype === "application/pdf") return <FileType className="h-5 w-5 text-muted-foreground" />;
  return <FileText className="h-5 w-5 text-muted-foreground" />;
};

/**
 * Read-only chip row rendered alongside a sent message — lets the user re-download
 * the file they originally attached. Each click hits the download endpoint with the
 * server-issued, short-lived token and triggers a browser download.
 */
export const MessageAttachmentList: React.FC<MessageAttachmentListProps> = ({ attachments, className }) => {
  if (!attachments || attachments.length === 0) return null;

  const handleDownload = async (attachment: MessageAttachmentType) => {
    const headers = { ...getGlobalHeaders() };
    delete headers["Content-Type"];
    const orgId = localStorage.getItem("tz_current_org_id") || "default";
    // eslint-disable-next-line custom/no-hardcoded-brand-name -- HTTP header name matches backend
    headers["X-TierZero-Org-Id"] = orgId;
    const url = `${config.get("APP_WWW_URL")}/internal/chat/attachments/download?token=${encodeURIComponent(attachment.downloadToken)}`;
    try {
      const response = await fetch(url, { headers });
      if (!response.ok) return;
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = attachment.originalName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(blobUrl);
    } catch {
      // Download failure is non-fatal; user can retry.
    }
  };

  return (
    <div className={cn("flex flex-wrap gap-2", className)}>
      {attachments.map((a, idx) => (
        <button
          type="button"
          key={`${a.originalName}-${idx}`}
          onClick={() => handleDownload(a)}
          title={`Download ${a.originalName}`}
          className="group flex items-center gap-2 rounded-md border border-border bg-background hover:bg-foreground/5 transition-colors pl-1 pr-2 py-1 max-w-[240px] text-xs cursor-pointer"
        >
          <div className="flex-shrink-0 h-8 w-8 rounded bg-muted flex items-center justify-center">
            <FileIcon mimetype={a.mimetype} />
          </div>
          <div className="flex flex-col min-w-0 text-left flex-1">
            <span className="truncate text-foreground">{a.originalName}</span>
            <span className="text-[10px] text-muted-foreground">
              {a.size ? formatSize(a.size) : a.mimetype.split("/")[1]?.toUpperCase() || "FILE"}
            </span>
          </div>
          <Download className="h-3.5 w-3.5 text-muted-foreground/60 group-hover:text-foreground flex-shrink-0" />
        </button>
      ))}
    </div>
  );
};
